import { useMemo, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Button } from '@/components';
import { DownloadIcon } from '@/components/icons.tsx';
import { useAuthFetch } from '@/hooks';
import { getInitialsFromName } from '@/utils';
import { CONSULT_REPORT_TYPES } from '@dermalink/shared';
import type { ConsultReportCell, ConsultReportType, OrgAdminConsultReportResponse, OrgAdminOrganization } from '@dermalink/shared';
import { PracticeAdminHead } from './PracticeAdmin.tsx';
import { usePracticeAdmin } from './practiceAdminContext.ts';
import { CONSULT_TYPE_LABEL, buildConsultReport, consultReportCsv, hasRevenue } from './consultReport.ts';
import type { ReportLine } from './consultReport.ts';
import admin from './PracticeAdmin.module.css';
import styles from './PracticeReport.module.css';

const TYPE_DOT_CLASS: Record<ConsultReportType, string> = {
  new_consult:  styles.dotNew,
  prescription: styles.dotPrescription,
  follow_up:    styles.dotFollowUp,
};

const TYPE_BAR_CLASS: Record<ConsultReportType, string> = {
  new_consult:  styles.barNew,
  prescription: styles.barPrescription,
  follow_up:    styles.barFollowUp,
};

function formatEuro(cents: number): string {
  return (cents / 100).toLocaleString('nl-BE', { style: 'currency', currency: 'EUR', minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

export function PracticeReport() {
  const { org } = usePracticeAdmin();
  return <PracticeReportContent key={org.id} org={org} />;
}

function PracticeReportContent({ org }: { org: OrgAdminOrganization }) {
  const authFetch = useAuthFetch();
  const [year, setYear]           = useState(() => new Date().getFullYear());
  const [doctorSub, setDoctorSub] = useState<string | null>(null);

  const { data, isLoading, isError, isPlaceholderData } = useQuery<OrgAdminConsultReportResponse>({
    queryKey: ['org-admin-consult-report', org.id, year],
    queryFn:  async () => {
      const res = await authFetch(`/org-admin/organizations/${org.id}/consultation-report?year=${year}`);
      if (!res.ok) throw new Error('Failed to load report');
      return res.json();
    },
    placeholderData: keepPreviousData,
  });

  const report = useMemo(() => data && buildConsultReport(data, doctorSub), [data, doctorSub]);
  const summary = report && (report.total ?? report.groups[0]?.subtotal);

  function handleExport() {
    if (!report || !data) return;
    const blob = new Blob([consultReportCsv(report, data.year)], { type: 'text/csv;charset=utf-8' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = `consulten-${org.slug}-${data.year}${doctorSub ? `-${fileSlug(report.groups[0].doctorName)}` : ''}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className={admin.container}>
      <div className={admin.inner}>
        <PracticeAdminHead title="Consulten & omzet">
          {data && (
            <div className={styles.segmented} role="radiogroup" aria-label="Jaar">
              {data.years.map((y) => (
                <button key={y} type="button" role="radio" aria-checked={y === year} className={`${styles.segment}${y === year ? ` ${styles.segmentActive}` : ''}`} onClick={() => setYear(y)}>{y}</button>
              ))}
            </div>
          )}
          <Button variant="ghost" icon={<DownloadIcon size={16} />} onClick={handleExport} disabled={!report || isPlaceholderData}>Exporteer CSV</Button>
        </PracticeAdminHead>

        {isError && <p className="text-muted status-message">Het overzicht kon niet worden geladen.</p>}

        {isLoading && (
          <div className={styles.kpiGrid}>
            {[0, 1, 2, 3].map((i) => <div key={i} className={`${styles.kpiCard} ${styles.skeleton}`} />)}
          </div>
        )}

        {summary && <SummaryTiles line={summary} year={year} />}

        {report && data && (
          <section className={`${styles.card}${isPlaceholderData ? ` ${styles.stale}` : ''}`}>
            <div className={styles.cardHead}>
              <div>
                <span className={styles.cardKicker}>Per arts en per maand</span>
                <p className={styles.cardNote}>Afgeronde consulten per maand waarin ze gestart zijn. Een consult telt mee zodra het betaald is, of — voor een opvolgconsult — afgesloten.</p>
              </div>
              {data.doctors.length > 1 && (
                <select className={styles.doctorSelect} value={doctorSub ?? ''} onChange={(e) => setDoctorSub(e.target.value || null)} aria-label="Arts">
                  <option value="">Alle artsen</option>
                  {data.doctors.map((d) => <option key={d.cognito_sub} value={d.cognito_sub}>Dr. {d.name}</option>)}
                </select>
              )}
            </div>

            {report.groups.length === 0 ? (
              <p className={styles.empty}>Geen afgeronde consulten in {data.year}.</p>
            ) : (
              <div className={styles.tableWrap}>
                <table className={styles.grid}>
                  <thead>
                    <tr>
                      <th rowSpan={2} className={styles.stubHead} scope="col">Arts / maand</th>
                      {CONSULT_REPORT_TYPES.map((t) => (
                        <th key={t} colSpan={hasRevenue(t) ? 2 : 1} className={styles.groupHead} scope="colgroup"><span className={`${styles.dot} ${TYPE_DOT_CLASS[t]}`} />{CONSULT_TYPE_LABEL[t]}</th>
                      ))}
                      <th colSpan={2} className={`${styles.groupHead} ${styles.totalCol}`} scope="colgroup">Totaal</th>
                    </tr>
                    <tr>
                      {CONSULT_REPORT_TYPES.map((t) => <ColumnPair key={t} total={false} revenue={hasRevenue(t)} />)}
                      <ColumnPair total revenue />
                    </tr>
                  </thead>
                  {report.groups.map((g) => (
                    <tbody key={g.doctorName} className={styles.group}>
                      <tr className={styles.doctorRow}>
                        <th scope="row" className={styles.stub}>
                          <span className={styles.doctorCell}>
                            <span className={styles.initials} aria-hidden="true">{getInitialsFromName(g.doctorName)}</span>
                            <span className={styles.doctorName}>{g.doctorName}</span>
                          </span>
                        </th>
                        <LineCells line={g.subtotal} />
                      </tr>
                      {g.months.map((m) => (
                        <tr key={m.label} className={styles.monthRow}>
                          <th scope="row" className={`${styles.stub} ${styles.monthStub}`}>{m.label}</th>
                          <LineCells line={m} />
                        </tr>
                      ))}
                    </tbody>
                  ))}
                  {report.total && (
                    <tfoot>
                      <tr className={styles.totalRow}>
                        <th scope="row" className={styles.stub}>{report.total.label}</th>
                        <LineCells line={report.total} />
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            )}
          </section>
        )}
      </div>
    </div>
  );
}

function ColumnPair({ total, revenue }: { total: boolean; revenue: boolean }) {
  const cls = `${styles.subHead}${total ? ` ${styles.totalCol}` : ''}`;
  return (
    <>
      <th className={`${cls} ${styles.pairStart} ${styles.countCol}`} scope="col">Aantal</th>
      {revenue && <th className={`${cls} ${styles.revenueCol}`} scope="col">Omzet</th>}
    </>
  );
}

function LineCells({ line }: { line: ReportLine }) {
  return (
    <>
      {CONSULT_REPORT_TYPES.map((t) => <CellPair key={t} cell={line.cells[t]} revenue={hasRevenue(t)} />)}
      <CellPair cell={line.total} total />
    </>
  );
}

function CellPair({ cell, total = false, revenue = true }: { cell: ConsultReportCell; total?: boolean; revenue?: boolean }) {
  const cls = `${styles.num}${total ? ` ${styles.totalCol}` : ''}`;
  return (
    <>
      <td className={`${cls} ${styles.pairStart}`}>{cell.count ? cell.count : <span className={styles.zero}>–</span>}</td>
      {revenue && <td className={cls}>{cell.revenue_cents ? formatEuro(cell.revenue_cents) : <span className={styles.zero}>–</span>}</td>}
    </>
  );
}

function SummaryTiles({ line, year }: { line: ReportLine; year: number }) {
  const revenue = line.total.revenue_cents;
  return (
    <div className={styles.kpiGrid}>
      <div className={`${styles.kpiCard} ${styles.kpiRevenue}`}>
        <span className={styles.kpiLabel}>Omzet {year}</span>
        <span className={styles.kpiValue}>{formatEuro(revenue)}</span>
        <div className={styles.shareBar} aria-hidden="true">
          {revenue > 0 && CONSULT_REPORT_TYPES.map((t) => line.cells[t].revenue_cents > 0 && (
            <span key={t} className={TYPE_BAR_CLASS[t]} style={{ flexGrow: line.cells[t].revenue_cents }} />
          ))}
        </div>
        <span className={styles.kpiNote}>{line.total.count} {line.total.count === 1 ? 'consult' : 'consulten'} in totaal</span>
        {line.total.platform_fee_cents > 0 && (
          <span className={styles.kpiNote}>
            Platformvergoeding {formatEuro(line.total.platform_fee_cents)} · netto {formatEuro(revenue - line.total.platform_fee_cents)}
          </span>
        )}
      </div>
      {CONSULT_REPORT_TYPES.map((t) => (
        <div key={t} className={styles.kpiCard}>
          <span className={styles.kpiLabel}><span className={`${styles.dot} ${TYPE_DOT_CLASS[t]}`} />{CONSULT_TYPE_LABEL[t]}</span>
          <span className={styles.kpiValue}>{line.cells[t].count}</span>
          <span className={styles.kpiNote}>{t === 'follow_up' ? 'Zonder bijdrage' : formatEuro(line.cells[t].revenue_cents)}</span>
        </div>
      ))}
    </div>
  );
}

function fileSlug(s: string): string {
  return s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}
